import type { ReactNode } from 'react'
import { Button } from '../Button'
import './StatusScreen.css'

/**
 * The three whole-screen states a data-driven view can be in besides
 * "showing content": loading, failed, and loaded-but-empty.
 *
 * They live in one module because they share a layout and are almost always
 * considered together at a call site — splitting them into three folders
 * would add navigation cost without adding clarity.
 *
 * Note the ARIA split: loading and empty are `status` (polite — the user is
 * not blocked and does not need interrupting), while an error is `alert`
 * (assertive — something needs attention).
 */

export interface LoadingScreenProps {
  message: string
}

export function LoadingScreen({ message }: LoadingScreenProps) {
  return (
    <div className="status-screen" role="status">
      {/* Decorative: the message beside it already carries the meaning. */}
      <div className="status-screen__spinner" aria-hidden="true" />
      <p className="status-screen__title">{message}</p>
    </div>
  )
}

export interface ErrorStateProps {
  title: string
  detail?: string
  /** Omit to render without a retry affordance. */
  onRetry?: () => void
  /** A way out, for screens outside the app frame where there are no tabs — typically a `<Link>`. */
  action?: ReactNode
}

export function ErrorState({ title, detail, onRetry, action }: ErrorStateProps) {
  return (
    <div className="status-screen status-screen--error" role="alert">
      <div className="status-screen__icon" aria-hidden="true">
        ⚠
      </div>
      <p className="status-screen__title">{title}</p>
      {detail ? <p className="status-screen__detail">{detail}</p> : null}
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
      {action ?? null}
    </div>
  )
}

export interface EmptyStateProps {
  title: string
  detail?: string
  /** Rendered after the detail — typically a `<Link>` back to somewhere useful. */
  action?: ReactNode
}

export function EmptyState({ title, detail, action }: EmptyStateProps) {
  return (
    <div className="status-screen" role="status">
      <div className="status-screen__icon" aria-hidden="true">
        ∅
      </div>
      <p className="status-screen__title">{title}</p>
      {detail ? <p className="status-screen__detail">{detail}</p> : null}
      {action ?? null}
    </div>
  )
}
